import { useEffect, useRef } from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { MediumCustomIcon } from "../../layer00";

export type CardPaginationBarProps = {
  page: number;
  totalPages: number;
  onNavigate: (page: number) => void;
  px?: string;
};

export const CardPaginationBar = ({
  page,
  totalPages,
  onNavigate,
  px = "1rem",
}: CardPaginationBarProps) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const userNavigated = useRef(false);
  const savedViewportY = useRef(0);

  const handleNavigate = (newPage: number) => {
    if (ref.current) {
      savedViewportY.current = ref.current.getBoundingClientRect().top;
    }
    userNavigated.current = true;
    onNavigate(newPage);
  };

  useEffect(() => {
    if (userNavigated.current && ref.current) {
      userNavigated.current = false;
      const delta =
        ref.current.getBoundingClientRect().top - savedViewportY.current;
      if (Math.abs(delta) > 1) {
        window.scrollBy({ top: delta, behavior: "instant" });
      }
    }
  }, [page]);

  return (
    <Box
      ref={ref}
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      columnGap=".25rem"
      px={px}
      py=".75rem"
      className="border-t border-[#8080801f]"
      mt="1rem"
    >
      <Typography variant="cardBodyRegular" color="text.secondary" mr=".25rem">
        {page + 1} / {totalPages}
      </Typography>
      <IconButton
        size="small"
        sx={{ border: "none" }}
        disabled={page === 0}
        onClick={() => handleNavigate(page - 1)}
      >
        <Box sx={{ transform: "rotate(-90deg)", display: "flex" }}>
          <MediumCustomIcon
            icon="CHEVRONUP"
            stroke={
              page === 0
                ? theme.palette.action.disabled
                : theme.palette.secondary.main
            }
          />
        </Box>
      </IconButton>
      <IconButton
        size="small"
        sx={{ border: "none" }}
        disabled={page >= totalPages - 1}
        onClick={() => handleNavigate(page + 1)}
      >
        <Box sx={{ transform: "rotate(90deg)", display: "flex" }}>
          <MediumCustomIcon
            icon="CHEVRONUP"
            stroke={
              page >= totalPages - 1
                ? theme.palette.action.disabled
                : theme.palette.secondary.main
            }
          />
        </Box>
      </IconButton>
    </Box>
  );
};
